import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Anchor, ArrowLeft, CreditCard, Key, LogOut, Monitor, Loader2, CheckCircle2, Crown } from "lucide-react";
import { toast } from "sonner";

const PLAN_DETAILS = {
  free: { label: "Free Trial", color: "bg-slate-100 text-slate-700" },
  weekly: { label: "Weekly Pass", color: "bg-teal-100 text-teal-700" },
  monthly: { label: "Monthly Plan", color: "bg-blue-100 text-blue-700" },
  all_access: { label: "All Access", color: "bg-amber-100 text-amber-700" },
};

const PLANS = [
  { type: "weekly", name: "Weekly Pass", amount: 199, period: "week" },
  { type: "monthly", name: "Monthly Plan", amount: 399, period: "month" },
  { type: "all_access", name: "All Access Bundle", amount: 599, period: "month", best: true },
];

export default function AccountPage() {
  const { user, logout, refreshUser, setUser } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);
  const [subscribing, setSubscribing] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);

  useEffect(() => {
    refreshUser();
    api.get("/account/billing-history").then(({ data }) => setBillingHistory(data)).catch(() => {});
  }, [refreshUser]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) return toast.error("New password must be at least 6 characters");
    setChangingPassword(true);
    try {
      await api.put("/account/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleLogoutAll = async () => {
    try {
      const { data } = await api.post("/account/logout-all-devices");
      sessionStorage.setItem("cop_session_token", data.new_session_token);
      toast.success("All other devices logged out");
    } catch {
      toast.error("Failed to logout other devices");
    }
  };

  const handleSubscribe = async (planType) => {
    setSubscribing(planType);
    try {
      const { data } = await api.post("/payments/create-order", { plan_type: planType });
      const options = {
        key: data.razorpay_key_id,
        amount: data.amount,
        currency: data.currency,
        name: "CalmOceanPrep",
        description: `${planType.replace("_", " ")} subscription`,
        order_id: data.order_id,
        handler: async (response) => {
          try {
            await api.post("/payments/verify", {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_subscription_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
            });
            toast.success("Plan upgraded successfully!");
            const updated = await refreshUser();
            if (updated) setUser(updated);
          } catch {
            toast.error("Payment verification failed. Contact support.");
          }
        },
        prefill: { email: user?.email },
        theme: { color: "#0d9488" },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => toast.error("Payment failed. Please try again."));
      rzp.open();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to initiate payment");
    } finally {
      setSubscribing(null);
    }
  };

  const pd = PLAN_DETAILS[user?.plan] || PLAN_DETAILS.free;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="cop-glass border-b px-4 py-3 flex items-center gap-3 sticky top-0 z-30" data-testid="account-header">
        <Button variant="ghost" size="sm" className="p-1" onClick={() => navigate("/dashboard")} data-testid="account-back-btn">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <Anchor className="h-4 w-4 text-primary" />
        <span className="text-sm font-medium">Account & Billing</span>
      </header>

      <main className="container mx-auto px-4 max-w-lg py-6 space-y-4" data-testid="account-main">
        {/* Current Plan */}
        <Card className="border" data-testid="current-plan-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold">Current Plan</h3>
              <Badge className={pd.color}>{pd.label}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">{user?.email}</p>
          </CardContent>
        </Card>

        {/* Upgrade Plans */}
        {user?.plan === "free" || user?.plan === "weekly" ? (
          <Card className="border" data-testid="upgrade-plans-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Crown className="h-4 w-4 text-amber-500" /> Upgrade Plan
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {PLANS.map((p) => (
                <div key={p.type} className={`p-3 rounded-lg border ${p.best ? "border-primary bg-teal-50" : ""} flex items-center justify-between`} data-testid={`plan-option-${p.type}`}>
                  <div>
                    <p className="text-sm font-medium">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.amount}/{p.period}</p>
                  </div>
                  <Button
                    size="sm"
                    variant={p.best ? "default" : "outline"}
                    disabled={subscribing === p.type || user?.plan === p.type}
                    onClick={() => handleSubscribe(p.type)}
                    data-testid={`subscribe-${p.type}-btn`}
                  >
                    {subscribing === p.type ? <Loader2 className="h-4 w-4 animate-spin" /> :
                      user?.plan === p.type ? <><CheckCircle2 className="h-3 w-3 mr-1" />Current</> : "Subscribe"}
                  </Button>
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}

        {/* Payment History */}
        <Card className="border" data-testid="billing-history-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Payment History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {billingHistory.length === 0 ? (
              <p className="text-sm text-muted-foreground">No payments yet.</p>
            ) : (
              <div className="space-y-2">
                {billingHistory.map((h) => (
                  <div key={h.id} className="flex justify-between text-sm py-1">
                    <span>{h.plan_type} - {h.amount}</span>
                    <span className="text-muted-foreground">{new Date(h.created_at).toLocaleDateString("en-IN")}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Separator />

        {/* Change Password */}
        <Card className="border" data-testid="change-password-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Key className="h-4 w-4" /> Change Password
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Current Password</Label>
                <Input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="current-password-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">New Password</Label>
                <Input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="new-password-input" />
              </div>
              <Button type="submit" size="sm" disabled={changingPassword} data-testid="change-password-btn">
                {changingPassword ? <Loader2 className="h-4 w-4 animate-spin" /> : "Change Password"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Device Management */}
        <Card className="border" data-testid="device-management-card">
          <CardContent className="p-4 space-y-3">
            <Button variant="outline" size="sm" className="w-full" onClick={handleLogoutAll} data-testid="logout-all-btn">
              <Monitor className="h-4 w-4 mr-2" /> Log Out All Devices
            </Button>
            <Button variant="destructive" size="sm" className="w-full" onClick={() => { logout(); navigate("/"); }} data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-2" /> Log Out
            </Button>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
